import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils';
import { routes } from '../../lib/routes';
import { Spinner } from '../../components/common';

/**
 * Landing for the single-use magic link in connection-notification emails.
 * The email button points here as /connect?hc=<code>. We PEEK the code without
 * consuming it to show whose account it opens, and only on the user's confirm
 * do we EXCHANGE it for a fresh session and drop them on their matches. The code
 * is single-use and short-lived, so a forwarded or stale link fails closed to an
 * expired screen (the same place a normal visitor to a protected page lands).
 */
type Phase = 'loading' | 'confirm' | 'exchanging' | 'failed';

const ConnectSignInPage: React.FC = () => {
  const navigate = useNavigate();
  const { checkStatus } = useAuth();
  const [phase, setPhase] = useState<Phase>('loading');
  const [identity, setIdentity] = useState<{ name: string; email: string } | null>(
    null,
  );
  const code = useRef<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // peek exactly once
    ran.current = true;
    const hc = new URLSearchParams(window.location.search).get('hc');
    if (!hc) {
      setPhase('failed');
      return;
    }
    code.current = hc;
    (async () => {
      try {
        const res = await api.post('/auth/relay/peek', { code: hc });
        const who = res?.data?.result ?? res?.data;
        if (!who?.email) throw new Error('no identity for code');
        setIdentity({ name: who.name || who.email, email: who.email });
        setPhase('confirm');
      } catch {
        setPhase('failed');
      }
    })();
  }, []);

  const onContinue = async () => {
    if (!code.current) {
      setPhase('failed');
      return;
    }
    setPhase('exchanging');
    try {
      const res = await api.post('/auth/relay/exchange', { code: code.current });
      const token = res?.data?.result?.access_token as string | undefined;
      if (!token) throw new Error('relay exchange returned no token');
      localStorage.setItem('auth-token', token);
      // Strip the one-time code from the URL/history immediately.
      window.history.replaceState({}, '', window.location.pathname);
      await checkStatus();
      navigate(routes.matches, { replace: true });
    } catch {
      setPhase('failed');
    }
  };

  if (phase === 'failed') {
    return (
      <div className='min-h-screen flex flex-col items-center justify-center px-6 text-center'>
        <h1 className='text-2xl font-heading font-medium text-secondary-500 mb-2'>
          This link expired
        </h1>
        <p className='text-body-500 mb-6 max-w-md'>
          Connection links are single-use and valid for a short time. Sign in to
          see who wants to connect with you.
        </p>
        <button
          onClick={() => navigate(routes.login, { replace: true })}
          className='text-primary-500 font-semibold'
        >
          Go to sign in
        </button>
      </div>
    );
  }

  if (phase === 'confirm' && identity) {
    return (
      <div className='min-h-screen flex flex-col items-center justify-center px-6 text-center'>
        <h1 className='text-2xl font-heading font-medium text-secondary-500 mb-2'>
          Open your matches
        </h1>
        <p className='text-body-500 mb-6 max-w-md'>
          Continue as <span className='font-semibold'>{identity.name}</span>
          <br />
          <span className='text-sm'>{identity.email}</span>
        </p>
        <button
          onClick={onContinue}
          className='bg-primary-500 text-white font-semibold rounded-xl px-6 py-3'
        >
          Continue to my matches
        </button>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex flex-col items-center justify-center gap-4'>
      <Spinner size='md' color='primary' />
      <p className='text-body-500'>
        {phase === 'exchanging' ? 'Signing you in...' : 'One moment...'}
      </p>
    </div>
  );
};

export default ConnectSignInPage;
